/**
 * AppLocale
 *
 * The closed set of locales the web app ships translations for. Adding a
 * language means adding a member here **and** a matching entry in the `locales`
 * array plus a `translations` key in `presentation/locale/index.ts` — the
 * `Record<AppLocale, l10n>` type makes a missing translation a compile error.
 *
 * **Web-only — there is no Flutter counterpart.** The Flutter app ships a single
 * hardcoded language and has no localization layer at all: a repo-wide grep of
 * `/lib` for `supportedLocales` / `localizationsDelegates` returns zero hits, and
 * `lib/main.dart` carries no `locale` wiring. So there is no Flutter enum to
 * mirror and no ordinal order to match — members follow the web's own existing
 * declaration order in `presentation/locale/index.ts` (`en`, then `ro`).
 *
 * **String-valued** because the value round-trips as a string, twice: it is
 * persisted verbatim to `localStorage('locale')` (`LocaleContext.tsx:35,45`) and
 * handed straight to the `Intl` APIs as a BCP-47 tag (`useDateFormatters.ts:14,17`
 * — `toLocaleTimeString(locale, …)` / `toLocaleDateString(locale, …)`). The
 * values must therefore stay exactly `'en'` / `'ro'` so already-persisted
 * `localStorage` entries keep resolving and `Intl` keeps receiving a valid tag.
 *
 * Promoted from the `Locale` string-literal union that previously lived in
 * `presentation/locale/index.ts`, per `conventions.md` §"Enums live in the domain
 * layer". Named `AppLocale` rather than `Locale` so it does not read as a shadow
 * of the DOM lib's `Intl.Locale`.
 */

export enum AppLocale {
  EN = 'en',
  RO = 'ro',
}

/**
 * The enum's own values, widened to `string[]` so an untrusted raw value can be
 * tested against them without first being asserted into the closed set.
 */
const APP_LOCALE_VALUES: readonly string[] = Object.values(AppLocale);

/**
 * Membership test for the closed `AppLocale` set — the sanctioned way a raw
 * string becomes an `AppLocale`.
 *
 * Every entry point where a locale arrives untyped goes through here rather than
 * through an `as AppLocale` cast: a persisted `localStorage('locale')` entry
 * (which may be stale, hand-edited, or written by a future app version), a URL
 * param, or a server-supplied user preference. Without the test, a non-member
 * such as `'fr'` or `'en-GB'` enters the set and `translations[locale]` — a
 * `Record<AppLocale, l10n>` lookup — silently yields `undefined` for every
 * consumer of `useLocale().l10n`.
 *
 * Callers keep ownership of the fallback policy (which language to use when the
 * answer is `false`); this only answers "is this value a member?".
 */
export const isAppLocale = (value: string | null | undefined): value is AppLocale =>
  value != null && APP_LOCALE_VALUES.includes(value);
